import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isWaiterConnected,
  listConnectedBranchWaiters,
  WAITER_PRESENCE_POLL_MS,
} from "../../services/supabase/waiterPresenceService";

export default function useConnectedWaiters(branchId, enabled) {
  const [state, setState] = useState({ branchId: "", waiters: [], loading: false, error: "" });
  const scope = useRef(null);
  const refreshRef = useRef(null);
  const assignmentVersion = useRef(0);
  const assignmentPending = useRef(false);
  const [assigning, setAssigning] = useState(false);
  const refresh = useCallback(() => refreshRef.current?.() || Promise.resolve([]), []);
  const cancelAssignment = useCallback(() => {
    assignmentVersion.current += 1;
    assignmentPending.current = false;
    setAssigning(false);
  }, []);
  const assign = useCallback(async (selectWaiter, onAssign, onUnavailable) => {
    if (assignmentPending.current) return;
    const token = scope.current;
    const version = ++assignmentVersion.current;
    assignmentPending.current = true;
    setAssigning(true);
    try {
      const waiters = await refresh();
      if (!token || scope.current !== token || assignmentVersion.current !== version) return;
      const connected = navigator.onLine === false ? [] : waiters.filter(
        (waiter) => isWaiterConnected(waiter, branchId)
      );
      const selected = selectWaiter(connected);
      if (!selected || !connected.some((waiter) => waiter.userId === selected.userId)) {
        setState((previous) => ({ ...previous,
          error: "El mozo ya no esta conectado. Elige otro mozo o registra una entrega manual.",
        }));
        onUnavailable?.();
        return;
      }
      await onAssign(selected);
    } finally {
      if (assignmentVersion.current === version) {
        assignmentPending.current = false;
        setAssigning(false);
      }
    }
  }, [branchId, refresh]);

  useEffect(() => {
    const token = {};
    scope.current = token;
    let pending = null;
    const current = () => scope.current === token;
    const load = () => {
      if (!current() || !enabled || !branchId) return Promise.resolve([]);
      if (pending) return pending;
      setState((previous) => {
        const waiters = previous.waiters.filter((waiter) => isWaiterConnected(waiter, branchId));
        return waiters.length === previous.waiters.length ? previous : { ...previous, waiters };
      });
      pending = Promise.resolve().then(async () => {
        try {
          if (navigator.onLine === false) throw new Error("Sin conexion");
          const waiters = await listConnectedBranchWaiters(branchId);
          if (!current()) return [];
          if (navigator.onLine === false) throw new Error("Sin conexion");
          setState({ branchId, waiters, loading: false, error: "" });
          return waiters;
        } catch (error) {
          if (current()) setState({
            branchId, waiters: [], loading: false,
            error: "No se pudo verificar que mozos estan conectados. Reintenta antes de asignar.",
          });
          return [];
        }
      }).finally(() => { pending = null; });
      return pending;
    };
    refreshRef.current = load;
    cancelAssignment();
    setState({ branchId, waiters: [], loading: Boolean(enabled && branchId), error: "" });
    void load();
    const timer = window.setInterval(() => { void load(); }, WAITER_PRESENCE_POLL_MS);
    const reload = () => { void load(); };
    const offline = () => setState({ branchId, waiters: [], loading: false, error: "Sin conexion. No se pueden asignar mozos." });
    const visible = () => { if (document.visibilityState === "visible") reload(); };
    window.addEventListener("focus", reload);
    window.addEventListener("online", reload);
    window.addEventListener("offline", offline);
    document.addEventListener("visibilitychange", visible);
    return () => {
      scope.current = null;
      assignmentVersion.current += 1;
      assignmentPending.current = false;
      refreshRef.current = null;
      window.clearInterval(timer);
      window.removeEventListener("focus", reload);
      window.removeEventListener("online", reload);
      window.removeEventListener("offline", offline);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [branchId, enabled, cancelAssignment]);

  const waiters = useMemo(() => enabled && state.branchId === branchId
    ? state.waiters.filter((waiter) => isWaiterConnected(waiter, branchId)) : [],
  [branchId, enabled, state]);
  return {
    waiters,
    loading: state.loading,
    error: state.branchId === branchId ? state.error : "",
    refresh,
    assign,
    assigning,
    cancelAssignment,
  };
}
